import { Segment } from '@dna/analytics'
import { storefrontClient } from '@dna/storefront'
import {
  addToCart,
  getLineItemProperties,
  getMasterSku,
  minorToMajorUnit,
  renderPrice,
  splitProductName,
} from '@dna/utils'

if (typeof window.WishlistPlusUILayoutAPI === 'undefined') {
  window.WishlistPlusUILayoutAPI = {}
}
if (typeof window.WishlistPlusListUIContext === 'undefined') {
  window.WishlistPlusListUIContext = {}
}
if (typeof window.WishlistPlusListUIExtensions === 'undefined') {
  window.WishlistPlusListUIExtensions = {}
}

WishlistPlusListUIContext.SwymWishlistPlusListActionTypes = {
  ManageListItem: 'ManageListItem',
  AddToCollection: 'AddToCollection',
  CreateCollection: 'CreateCollection',
  EditCollection: 'EditCollection',
}

WishlistPlusListUIContext.SwymWishlistPlusListNotificationStatusTypes = {
  Success: 'success',
  Error: 'error',
  Warning: 'Warning',
  Info: 'info',
  Neutral: 'neutral',
  Toast: 'toast',
}

WishlistPlusListUIContext.SwymWishlistPlusListUrls = {
  List: '#swym-list',
  Collections: '#swym-collections',
  CollectionList: '#swym-collection:',
}

WishlistPlusListUIContext.SwymWishlistPlusListNotificationIconType = {
  SuccessIcon: 'SuccessIcon',
  RemoveIcon: 'RemoveIcon',
}

WishlistPlusListUIContext.SwymWishlistPlusListShopifyProductData = {}

WishlistPlusListUIContext.SwymWishlistPlusListRenderStringTemplate = (
  template,
  values,
) => {
  return template.replace(/\${(.*?)}/g, (match, key) => {
    // biome-ignore lint/suspicious/noPrototypeBuiltins: <explanation>
    if (values.hasOwnProperty(key)) {
      return values[key]
    }
    console.warn(`Missing key: ${key}`)
    return ''
  })
}

WishlistPlusListUIContext.checkUserCanEditList = (list) => {
  if (!list) {
    console.warn('list not exist to check list access')
    return false
  }
  const isUserList = WishlistPlusListUIContext.WishlistListLists.find(
    (userList) => userList.lid === list.lid,
  )
  if (isUserList) {
    return true
  }
  return false
}

WishlistPlusListUIContext.FormatPrice = (value) => {
  try {
    if (window.SwymOverrideFormatMoneyFn) {
      return window.SwymOverrideFormatMoneyFn(value)
    }
    if (window._swat.platform.formatMoney) {
      if (window._swat.platform.currentMoneyFormat?.()) {
        return window._swat.platform.formatMoney(
          value * 100,
          window._swat.platform.currentMoneyFormat(),
        )
      }
    }
  } catch (err) {
    swat.utils.log(`Error formatting price - ${value}${err}`)
  }
  const currency = window._swat.currency
  return currency + window._swat.utils.padDecimal(value)
}

WishlistPlusListUIExtensions.calcDifferenceInHours = (
  hours,
  dateTimeString,
) => {
  try {
    if (!dateTimeString) {
      console.log('[WARNING] Date time string empty')
      return
    }
    const storedDateTime = new Date(dateTimeString?.trim())
    const currentDateTime = new Date()

    const differenceInMs = Math.abs(currentDateTime - storedDateTime)
    const differenceInHours = differenceInMs / (1000 * 60 * 60)
    return differenceInHours > hours
  } catch (error) {
    console.log('[Error/Warning] while performing health check', error)
    return false
  }
}

class BaseSwymWishlist extends HTMLElement {
  constructor() {
    super()
    this.wishlist = null
    this.collections = []
    this.elements = {}
    this.initUi()
    this.initSwymCallbacks()
  }

  initUi() {
    this.setAttribute('id', 'swym-wishlist-plus-list')
    window.addEventListener('hashchange', () => {
      this.checkForSwymDrawerHash()
    })

    document.addEventListener(
      WishlistPlusListUIContext.CustomEvents?.SwymWishlistFetched,
      () => {
        this.checkForSwymDrawerHash()
      },
    )

    WishlistPlusListUIExtensions.SwymWishlistPlusListNotification =
      document.querySelector('swym-wishlist-plus-list-ui-notification')
  }

  reRenderWishlistListUI() {
    this.reRenderUI()
    this.initElement()
    this.renderWishlistAndCollections()
  }

  initElement() {
    WishlistPlusListUIExtensions.SwymWishlistPlusList = document.querySelector(
      '#swym-wishlist-plus-list',
    )
    WishlistPlusListUIExtensions.SwymWishlistPlusListActions =
      document.querySelector('#swym-wishlist-plus-list-ui-actions') ||
      document.querySelector('#swym-wishlist-plus-list-ui-actions-target-page')

    WishlistPlusListUIExtensions.wishlistListComponent = document.querySelector(
      'swym-wishlist-plus-list-ui-default-wishlist',
    )
    WishlistPlusListUIExtensions.collectionsCarouselComponent =
      document.querySelector('swym-wishlist-plus-list-ui-collection-carousel')
    WishlistPlusListUIExtensions.collectionsListComponent =
      document.querySelector('swym-wishlist-plus-list-ui-collection-list')
  }

  loadStrings() {
    try {
      const RetailerStrings =
        window._swat?.retailerSettings?.Strings?.[Shopify.locale] ||
        window._swat?.retailerSettings?.Strings ||
        {}
      const {
        ThemeAppStorefrontLayoutTitle,

        ThemeAppStorefrontLayoutAddToCartCTA,
        ThemeAppStorefrontLayoutAddedToCartCTA,
        ThemeAppStorefrontLayoutAddingToCartCTA,
        ThemeAppStorefrontLayoutSoldoutCTA,
        ThemeAppStorefrontLayoutViewCartCTA,
        ThemeAppStorefrontLayoutMoveToCartCTA,
        ThemeAppStorefrontLayoutMoveingToCartCTA,
        ThemeAppStorefrontLayoutRemoveSFLItemCTA,

        ThemeAppStorefrontLayoutLoginHeading,
        ThemeAppStorefrontLayoutLoginMessage,
        ThemeAppStorefrontLayoutLoginButtonCTA,
        ThemeAppStorefrontLayoutLoggedUserWelcomeMessage,

        ThemeAppStorefrontLayoutWishlistTitle,
        ThemeAppStorefrontLayoutWishlistInfo,
        ThemeAppStorefrontLayoutEmptyWishlistTitle,
        ThemeAppStorefrontLayoutEmptyWishlistDescription,

        ThemeAppStorefrontLayoutCollectionsTitle,
        ThemeAppStorefrontLayoutEmptyCarouselCollectionsDescription,
        ThemeAppStorefrontLayoutEmptyCollectionsTitle,
        ThemeAppStorefrontLayoutEmptyCollectionsDescription,

        ThemeAppStorefrontLayoutAddToCollectionsTitle,
        ThemeAppStorefrontLayoutAddToCollectionCTA,
        ThemeAppStorefrontLayoutCreateCollectionCTA,
        ThemeAppStorefrontLayoutSaveNewCollectionCTA,
        ThemeAppStorefrontLayoutRemoveItemCTA,
        ThemeAppStorefrontLayoutRenameCollectionCTA,
        ThemeAppStorefrontLayoutDeleteCollectionCTA,
        ThemeAppStorefrontLayoutShareCollectionCTA,
        ThemeAppStorefrontLayoutSaveCollectionCTA,
        ThemeAppStorefrontLayoutEditCollectionCTA,
        ThemeAppStorefrontLayoutShareCollectionTitle,
        ThemeAppStorefrontLayoutShareCollectionMessage,
        ThemeAppStorefrontLayoutSharedCollectionMessage,
        ThemeAppStorefrontLayoutUpdateCollectionTitle,

        ThemeAppStorefrontLayoutErrorMessageListNameRequired,
        ThemeAppStorefrontLayoutErrorMessageListNameRequired3Char,
        ThemeAppStorefrontLayoutErrorMessageListNameAlreadyExist,

        ThemeAppStorefrontLayoutNotificationMessageItemSaved,
        ThemeAppStorefrontLayoutNotificationMessageItemRemoved,
        ThemeAppStorefrontLayoutNotificationMessageAddedToCart,
        ThemeAppStorefrontLayoutNotificationMessageAddedToCollection,
        ThemeAppStorefrontLayoutNotificationMessageCollectionSaved,
        ThemeAppStorefrontLayoutNotificationMessageCollectionDeleted,
        ThemeAppStorefrontLayoutNotificationMessageCollectionUpdated,
        ThemeAppStorefrontLayoutNotificationMessageCollectionUnavailable,
        ThemeAppStorefrontLayoutNotificationMessageSFLItemSaved,
        ThemeAppStorefrontLayoutNotificationMessageSFLItemRemoved,
        ThemeAppStorefrontLayoutNotificationMessageMovedToCart,

        ThemeAppStorefrontLayoutNotificationActionAddToCollection,
        ThemeAppStorefrontLayoutNotificationActionView,
        ThemeAppStorefrontLayoutNotificationActionViewCollection,
        ThemeAppStorefrontLayoutNotificationActionGoToCart,

        ThemeAppStorefrontLayoutTextItem,
        ThemeAppStorefrontLayoutTextItems,

        ThemeAppStorefrontLayoutTabWishlist,
        ThemeAppStorefrontLayoutTabSavedForLater,
        ThemeAppStorefrontLayoutSaveForLaterTitle,
        ThemeAppStorefrontLayoutEmptySavedForLaterTitle,
        ThemeAppStorefrontLayoutEmptySavedForLaterDescription,
      } = RetailerStrings

      WishlistPlusListUIContext.SwymWishlistPlusListStrings = {
        ...WishlistPlusListUIContext?.SwymWishlistPlusListStrings,
        ...(ThemeAppStorefrontLayoutTitle
          ? { title: ThemeAppStorefrontLayoutTitle }
          : {}),

        ...(ThemeAppStorefrontLayoutAddToCartCTA
          ? { addToCart: ThemeAppStorefrontLayoutAddToCartCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutAddedToCartCTA
          ? { addedToCart: ThemeAppStorefrontLayoutAddedToCartCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutAddingToCartCTA
          ? { addingToCart: ThemeAppStorefrontLayoutAddingToCartCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutSoldoutCTA
          ? { soldOut: ThemeAppStorefrontLayoutSoldoutCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutViewCartCTA
          ? { viewCartCta: ThemeAppStorefrontLayoutViewCartCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutMoveToCartCTA
          ? { moveToCartCta: ThemeAppStorefrontLayoutMoveToCartCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutMoveingToCartCTA
          ? { movingToCartCta: ThemeAppStorefrontLayoutMoveingToCartCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutRemoveSFLItemCTA
          ? { removeSflItemCta: ThemeAppStorefrontLayoutRemoveSFLItemCTA }
          : {}),

        ...(ThemeAppStorefrontLayoutLoginHeading
          ? { loginHeading: ThemeAppStorefrontLayoutLoginHeading }
          : {}),
        ...(ThemeAppStorefrontLayoutLoginMessage
          ? { loginText: ThemeAppStorefrontLayoutLoginMessage }
          : {}),
        ...(ThemeAppStorefrontLayoutLoginButtonCTA
          ? { loginButtonText: ThemeAppStorefrontLayoutLoginButtonCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutLoggedUserWelcomeMessage
          ? {
              loggedUserWelcomeMessage:
                ThemeAppStorefrontLayoutLoggedUserWelcomeMessage,
            }
          : {}),

        ...(ThemeAppStorefrontLayoutWishlistTitle
          ? { wishlistTitle: ThemeAppStorefrontLayoutWishlistTitle }
          : {}),
        ...(ThemeAppStorefrontLayoutWishlistInfo
          ? { wishlistInfo: ThemeAppStorefrontLayoutWishlistInfo }
          : {}),
        ...(ThemeAppStorefrontLayoutEmptyWishlistTitle
          ? { emptyWishlistTitle: ThemeAppStorefrontLayoutEmptyWishlistTitle }
          : {}),
        ...(ThemeAppStorefrontLayoutEmptyWishlistDescription
          ? {
              emptyWishlistDescription:
                ThemeAppStorefrontLayoutEmptyWishlistDescription,
            }
          : {}),

        ...(ThemeAppStorefrontLayoutCollectionsTitle
          ? { collectionTitle: ThemeAppStorefrontLayoutCollectionsTitle }
          : {}),
        ...(ThemeAppStorefrontLayoutEmptyCarouselCollectionsDescription
          ? {
              emptyCarouselCollectionText:
                ThemeAppStorefrontLayoutEmptyCarouselCollectionsDescription,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutEmptyCollectionsTitle
          ? {
              emptyCollectionText:
                ThemeAppStorefrontLayoutEmptyCollectionsTitle,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutEmptyCollectionsDescription
          ? {
              emptyCollectionDescription:
                ThemeAppStorefrontLayoutEmptyCollectionsDescription,
            }
          : {}),

        ...(ThemeAppStorefrontLayoutAddToCollectionsTitle
          ? {
              addToCollectionTitle:
                ThemeAppStorefrontLayoutAddToCollectionsTitle,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutAddToCollectionCTA
          ? { addToCollectionCta: ThemeAppStorefrontLayoutAddToCollectionCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutCreateCollectionCTA
          ? { createCollectionCta: ThemeAppStorefrontLayoutCreateCollectionCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutSaveNewCollectionCTA
          ? {
              saveNewCollectionCta:
                ThemeAppStorefrontLayoutSaveNewCollectionCTA,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutRemoveItemCTA
          ? { removeItemCta: ThemeAppStorefrontLayoutRemoveItemCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutRenameCollectionCTA
          ? { renameCollectionCta: ThemeAppStorefrontLayoutRenameCollectionCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutDeleteCollectionCTA
          ? { deleteCollectionCta: ThemeAppStorefrontLayoutDeleteCollectionCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutShareCollectionCTA
          ? { shareCollectionCta: ThemeAppStorefrontLayoutShareCollectionCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutSaveCollectionCTA
          ? { saveCollectionCta: ThemeAppStorefrontLayoutSaveCollectionCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutEditCollectionCTA
          ? { editCollectionCta: ThemeAppStorefrontLayoutEditCollectionCTA }
          : {}),
        ...(ThemeAppStorefrontLayoutShareCollectionTitle
          ? {
              shareCollectionTitle:
                ThemeAppStorefrontLayoutShareCollectionTitle,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutShareCollectionMessage
          ? {
              shareCollectionMessage:
                ThemeAppStorefrontLayoutShareCollectionMessage,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutSharedCollectionMessage
          ? {
              sharedCollectionMessage:
                ThemeAppStorefrontLayoutSharedCollectionMessage,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutUpdateCollectionTitle
          ? {
              updateCollectionTitle:
                ThemeAppStorefrontLayoutUpdateCollectionTitle,
            }
          : {}),

        ...(ThemeAppStorefrontLayoutErrorMessageListNameRequired
          ? {
              errorMessageListNameRequired:
                ThemeAppStorefrontLayoutErrorMessageListNameRequired,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutErrorMessageListNameRequired3Char
          ? {
              errorMessageListNameRequire3Char:
                ThemeAppStorefrontLayoutErrorMessageListNameRequired3Char,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutErrorMessageListNameAlreadyExist
          ? {
              errorMessageListNameAlreadyExist:
                ThemeAppStorefrontLayoutErrorMessageListNameAlreadyExist,
            }
          : {}),

        ...(ThemeAppStorefrontLayoutNotificationMessageItemSaved
          ? {
              notificationMessageItemSaved:
                ThemeAppStorefrontLayoutNotificationMessageItemSaved,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationMessageItemRemoved
          ? {
              notificationMessageItemRemoved:
                ThemeAppStorefrontLayoutNotificationMessageItemRemoved,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationMessageAddedToCart
          ? {
              notificationMessageAddedToCart:
                ThemeAppStorefrontLayoutNotificationMessageAddedToCart,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationMessageAddedToCollection
          ? {
              notificationMessageAddedToCollection:
                ThemeAppStorefrontLayoutNotificationMessageAddedToCollection,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationMessageCollectionSaved
          ? {
              notificationMessageCollectionSaved:
                ThemeAppStorefrontLayoutNotificationMessageCollectionSaved,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationMessageCollectionDeleted
          ? {
              notificationMessageCollectionDeleted:
                ThemeAppStorefrontLayoutNotificationMessageCollectionDeleted,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationMessageCollectionUpdated
          ? {
              notificationMessageCollectionUpdated:
                ThemeAppStorefrontLayoutNotificationMessageCollectionUpdated,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationMessageCollectionUnavailable
          ? {
              notificationMessageCollectionUnavailable:
                ThemeAppStorefrontLayoutNotificationMessageCollectionUnavailable,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationMessageSFLItemSaved
          ? {
              notificationMessageSFLItemSaved:
                ThemeAppStorefrontLayoutNotificationMessageSFLItemSaved,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationMessageSFLItemRemoved
          ? {
              notificationMessageSFLItemRemoved:
                ThemeAppStorefrontLayoutNotificationMessageSFLItemRemoved,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationMessageMovedToCart
          ? {
              notificationMessageMovedToCart:
                ThemeAppStorefrontLayoutNotificationMessageMovedToCart,
            }
          : {}),

        ...(ThemeAppStorefrontLayoutNotificationActionAddToCollection
          ? {
              notificationActionAddToCollection:
                ThemeAppStorefrontLayoutNotificationActionAddToCollection,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationActionView
          ? {
              notificationActionView:
                ThemeAppStorefrontLayoutNotificationActionView,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationActionViewCollection
          ? {
              notificationActionViewCollection:
                ThemeAppStorefrontLayoutNotificationActionViewCollection,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutNotificationActionGoToCart
          ? {
              notificationActionGoToCart:
                ThemeAppStorefrontLayoutNotificationActionGoToCart,
            }
          : {}),

        ...(ThemeAppStorefrontLayoutTextItem
          ? { item: ThemeAppStorefrontLayoutTextItem }
          : {}),
        ...(ThemeAppStorefrontLayoutTextItems
          ? { items: ThemeAppStorefrontLayoutTextItems }
          : {}),

        ...(ThemeAppStorefrontLayoutTabWishlist
          ? { tabWishlist: ThemeAppStorefrontLayoutTabWishlist }
          : {}),
        ...(ThemeAppStorefrontLayoutTabSavedForLater
          ? { tabSavedForLater: ThemeAppStorefrontLayoutTabSavedForLater }
          : {}),
        ...(ThemeAppStorefrontLayoutSaveForLaterTitle
          ? { savedForLaterTitle: ThemeAppStorefrontLayoutSaveForLaterTitle }
          : {}),
        ...(ThemeAppStorefrontLayoutEmptySavedForLaterTitle
          ? {
              emptySavedForLaterTitle:
                ThemeAppStorefrontLayoutEmptySavedForLaterTitle,
            }
          : {}),
        ...(ThemeAppStorefrontLayoutEmptySavedForLaterDescription
          ? {
              emptySavedForLaterDescription:
                ThemeAppStorefrontLayoutEmptySavedForLaterDescription,
            }
          : {}),
      }
      this.reRenderWishlistListUI()
    } catch (error) {
      window._swat?.utils.log(error)
    }
  }

  initSwymCallbacks() {
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = []
    }
    window.SwymCallbacks.push(this.swymCallbackFn.bind(this))
  }

  swymCallbackFn(swat) {
    if (
      WishlistPlusListUIContext.WishlistPlusListConfig
        ?.EnableWishlistPlusListOnLauncher
    ) {
      swat.ui.open = () => {
        window.location.hash =
          WishlistPlusListUIContext.SwymWishlistPlusListUrls?.List
      }
    }

    if (
      WishlistPlusListUIContext.WishlistPlusListConfig
        ?.EnableWishlistPlusListNotification
    ) {
      //override swym default notification for enbale only custom notificaiton
      swat.ui.uiRef.showNotification = (notification) => {
        console.log('swym notification detail', notification)
      }
    }

    swat.evtLayer.addEventListener(swat.JSEvents.StringsLoaded, (event) => {
      this.loadStrings()
    })

    this.attachSwymEventListeners(swat)
    if (!this.wishlist || !this.collections) {
      this.wishlist = WishlistPlusListUIContext.WishlistListDefaultList
      this.collections = WishlistPlusListUIContext.WishlistListCollections
    }
    if (!this.wishlist || !this.collections) {
      this.refreshList()
    }
  }

  async checkForSwymDrawerHash() {
    const { WishlistPlusListType, WishlistPlusListAsSectionPageURL } =
      WishlistPlusListUIContext.WishlistPlusListConfig
    const currentHash = window.location.hash

    const openWishlistPage = () => {
      if (
        WishlistPlusListType === 'as-drawer' ||
        WishlistPlusListType === 'as-modal'
      ) {
        WishlistPlusListUIExtensions.SwymWishlistPlusList?.open()
      } else if (
        WishlistPlusListType === 'as-section' &&
        window.location.pathname !== WishlistPlusListAsSectionPageURL
      ) {
        const locale = window.Shopify.routes.root
        const formattedLocale = locale === '/' ? '' : locale.replace(/\/$/, '')
        window.location = `${window.location.origin}${formattedLocale}${WishlistPlusListAsSectionPageURL}${window.location.hash}`
      }
    }

    if (
      currentHash === WishlistPlusListUIContext.SwymWishlistPlusListUrls?.List
    ) {
      WishlistPlusListUIExtensions.collectionsListComponent?.closeCollectinListView()
      openWishlistPage()
    } else if (
      currentHash.startsWith(
        WishlistPlusListUIContext.SwymWishlistPlusListUrls?.CollectionList,
      )
    ) {
      const collectionId = currentHash.slice(
        WishlistPlusListUIContext.SwymWishlistPlusListUrls?.CollectionList
          .length,
      )
      WishlistPlusListUIExtensions.collectionsListComponent?.openCollectionListView()

      if (
        WishlistPlusListUIContext.WishlistListCollections &&
        WishlistPlusListUIContext.WishlistListDefaultList
      ) {
        const selectedCollection =
          WishlistPlusListUIContext.WishlistListCollections?.find(
            (collection) => collection.lid === collectionId,
          )

        if (selectedCollection) {
          WishlistPlusListUIExtensions.collectionsListComponent?.setData({
            list: selectedCollection,
            collections: WishlistPlusListUIContext.WishlistListCollections,
          })
        } else {
          const sharedListDetails =
            await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.fetchListDetails(
              collectionId,
            )
          const { list } = sharedListDetails || {}
          if (list) {
            WishlistPlusListUIExtensions.collectionsListComponent?.setData({
              list,
              collections: WishlistPlusListUIContext.WishlistListCollections,
            })
          }
        }

        openWishlistPage()
      }
    }
  }

  attachSwymEventListeners(swat) {
    swat.evtLayer.addEventListener('sw:addedtowishlist', (event) => {
      this.refreshList(swat)

      const image = event?.detail?.d?.iu || null

      WishlistPlusListUIExtensions.SwymWishlistPlusListNotification?.setMessage(
        {
          message:
            WishlistPlusListUIContext.SwymWishlistPlusListStrings
              ?.notificationMessageItemSaved,
          status:
            WishlistPlusListUIContext
              .SwymWishlistPlusListNotificationStatusTypes?.Toast,
          duration: 5000,
          image,
          action: {
            label:
              WishlistPlusListUIContext.SwymWishlistPlusListStrings
                ?.notificationActionView,
            onClick: () => {
              window.location.hash =
                WishlistPlusListUIContext.SwymWishlistPlusListUrls?.List
            },
          },
        },
      )
    })

    swat.evtLayer.addEventListener('sw:removedfromwishlist', (event) => {
      this.refreshList()

      const image = event?.detail?.d?.iu || null

      WishlistPlusListUIExtensions.SwymWishlistPlusListNotification?.setMessage(
        {
          message:
            WishlistPlusListUIContext.SwymWishlistPlusListStrings
              ?.notificationMessageItemRemoved,
          status:
            WishlistPlusListUIContext
              .SwymWishlistPlusListNotificationStatusTypes?.Toast,
          duration: 5000,
          image,
        },
      )
    })
  }

  refreshList() {
    this.fetchWishlist()
  }

  async fetchWishlist() {
    try {
      let lists = await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.updateList()
      lists = lists.sort((a, b) => b.cts - a.cts)

      this.wishlist = WishlistPlusListUIContext.WishlistListDefaultList
      this.collections = WishlistPlusListUIContext.WishlistListCollections

      if (this.wishlist?.listcontents) {
        this.wishlist.listcontents = this.wishlist.listcontents.sort(
          (a, b) => b.cts - a.cts,
        )
      }

      for (const collection of this.collections) {
        if (collection.listcontents) {
          collection.listcontents = collection.listcontents.sort(
            (a, b) => b.cts - a.cts,
          )
        }
      }

      this.renderWishlistAndCollections()
    } catch (error) {
      console.warn('Failed to fetch swym wishlist', error)
    }
  }

  renderWishlistAndCollections() {
    if (this.wishlist || this.collections) {
      if (
        window.location.hash.startsWith(
          WishlistPlusListUIContext.SwymWishlistPlusListUrls?.CollectionList,
        )
      ) {
        const collectionId = window.location.hash.slice(
          WishlistPlusListUIContext.SwymWishlistPlusListUrls?.CollectionList
            .length,
        )
        if (
          WishlistPlusListUIExtensions.collectionsListComponent &&
          this.collections
        ) {
          const selectedCollection = this.collections.find(
            (collection) => collection.lid === collectionId,
          )
          WishlistPlusListUIExtensions.collectionsListComponent?.setData({
            list: selectedCollection,
            collections: this.collections,
          })
        }
      }

      WishlistPlusListUIExtensions.wishlistListComponent?.setData({
        wishlist: this.wishlist,
        collections: this.collections,
      })

      if (
        WishlistPlusListUIExtensions.collectionsCarouselComponent &&
        this.collections
      ) {
        WishlistPlusListUIExtensions.collectionsCarouselComponent?.setData({
          collections: this.collections,
        })
      }
    }
  }
}

class SwymWishlistPlusListAsModal extends BaseSwymWishlist {
  constructor() {
    super()
    this.renderUI()
  }

  renderUI() {
    const collectionClass = window.location.hash.startsWith(
      WishlistPlusListUIContext.SwymWishlistPlusListUrls?.CollectionList,
    )
      ? 'swym-wishlist-plus-list-ui-show-collection-list'
      : ''
    this.innerHTML = `
          <div id="swym-wishlist-plus-list-ui-container" class="swym-wishlist-plus-list-ui-drawer swym-wishlist-plus-list-ui-hide-view">
          <div class="swym-wishlist-plus-list-ui-backdrop"></div>
          <div class="swym-wishlist-plus-list-ui-layout">
            <button id="swym-wishlist-plus-list-ui-close-drawer-button" class="swym-wishlist-plus-list-ui-close-button">
              <svg data-v-bfb713a3="" data-v-e057eb37="" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" class="base-icon close-icon" aria-hidden="true"><path d="m10.536 1.5-9 9M1.536 1.5l9 9" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            </button>
            <div class="swym-wishlist-plus-list-ui-header">
              <div class="swym-wishlist-plus-list-ui-title">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.title}</div>
              <swym-wishlist-plus-list-ui-login-user showUser="true"></swym-wishlist-plus-list-ui-login-user>
            </div>
            <div class="swym-wishlist-plus-list-ui-container">
              <swym-wishlist-plus-list-ui-login-user showLogin="true"></swym-wishlist-plus-list-ui-login-user>
              <swym-wishlist-plus-list-ui-default-wishlist></swym-wishlist-plus-list-ui-default-wishlist>
            </div>
            <div class="swym-wishlist-plus-list-ui-empty-container"></div>
            <swym-wishlist-plus-list-ui-collection-list id="swym-wishlist-plus-list-ui-collection-list" class="${collectionClass}"></swym-wishlist-plus-list-ui-collection-list>
          </div>
        </div>
    `

    this.initUIElement()
  }

  initUIElement() {
    this.elements = {
      drawer: this.querySelector('.swym-wishlist-plus-list-ui-drawer'),
      drawerCloseButton: this.querySelector(
        '#swym-wishlist-plus-list-ui-close-drawer-button',
      ),
      drawerbackdrop: this.querySelector(
        '.swym-wishlist-plus-list-ui-backdrop',
      ),
      collectionList: this.querySelector(
        '#swym-wishlist-plus-list-ui-collection-list',
      ),
    }

    this.attachDrawerEventListner()
  }

  attachDrawerEventListner() {
    this.elements.drawerCloseButton.addEventListener(
      'click',
      this.close.bind(this),
    )
    this.elements.drawerbackdrop.addEventListener(
      'click',
      this.close.bind(this),
    )
  }

  open() {
    this.isOpen = true
    if (this.elements?.drawer) {
      this.elements.drawer.classList.remove(
        'swym-wishlist-plus-list-ui-hide-view',
      )
      document.body.classList.add('no-scroll')
    }
  }

  close() {
    this.isOpen = false
    if (this.elements?.drawer) {
      this.elements.drawer.classList.add('swym-wishlist-plus-list-ui-hide-view')
      document.body.classList.remove('no-scroll')
      history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search,
      )
    }
  }

  reRenderUI() {
    this.renderUI()
    if (this.isOpen) {
      this.open()
    }
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-as-modal',
  SwymWishlistPlusListAsModal,
)

class SwymWishlistPlusListAsSection extends BaseSwymWishlist {
  constructor() {
    super()
    this.renderUI()
  }

  renderUI() {
    this.listContainerToRender = null
    try {
      this.listContainerToRender = document.querySelector(
        WishlistPlusListUIContext.WishlistPlusListConfig
          .WishlistPlusListAsSectionContainerSelector,
      )

      if (this.listContainerToRender) {
        this.listContainerToRender.innerHTML = `
        <div id="swym-wishlist-plus-list-ui-container">
          <div id="swym-wishlist-plus-list-ui-section-container">
            <div class="swym-wishlist-plus-list-ui-header">
              <div class="swym-wishlist-plus-list-ui-title">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.title}</div>
              <swym-wishlist-plus-list-ui-login-user showUser="true"></swym-wishlist-plus-list-ui-login-user>
            </div>
            <div class="swym-wishlist-plus-list-ui-container">
              <swym-wishlist-plus-list-ui-login-user showLogin="true"></swym-wishlist-plus-list-ui-login-user>
              <swym-wishlist-plus-list-ui-default-wishlist></swym-wishlist-plus-list-ui-default-wishlist>
            </div>
            <div class="swym-wishlist-plus-list-ui-empty-container"></div>
            <swym-wishlist-plus-list-ui-collection-list id="swym-wishlist-plus-list-ui-collection-list"></swym-wishlist-plus-list-ui-collection-list>
          </div>
        </div>
        `
      }
    } catch (error) {
      console.warn(
        'Invalid List Container Selector ',
        WishlistPlusListUIContext.WishlistPlusListConfig
          .WishlistPlusListAsSectionContainerSelector,
      )
    }
  }

  reRenderUI() {
    this.renderUI()
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-as-section',
  SwymWishlistPlusListAsSection,
)

class SwymWishlistPlusListAsDrawer extends BaseSwymWishlist {
  constructor() {
    super()
    this.renderUI()
  }

  renderUI() {
    const collectionClass = window.location.hash.startsWith(
      WishlistPlusListUIContext.SwymWishlistPlusListUrls?.CollectionList,
    )
      ? 'swym-wishlist-plus-list-ui-show-collection-list'
      : ''
    this.innerHTML = `
          <div id="swym-wishlist-plus-list-ui-container" role="region" aria-labelledby="side-drawer" class="swym-wishlist-plus-list-ui-drawer swym-wishlist-plus-list-ui-hide-view swym-wishlist-plus-list-ui-drawer-position-${WishlistPlusListUIContext.WishlistPlusListConfig?.WishlistPlusListDrawerPosition}">
          <div class="swym-wishlist-plus-list-ui-backdrop"></div>
          <div class="swym-wishlist-plus-list-ui-layout">
            <button id="swym-wishlist-plus-list-ui-close-drawer-button" class="swym-wishlist-plus-list-ui-close-button">
              <svg data-v-bfb713a3="" data-v-e057eb37="" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" class="base-icon close-icon" aria-hidden="true"><path d="m10.536 1.5-9 9M1.536 1.5l9 9" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            </button>
            <div class="swym-wishlist-plus-list-ui-header">
              <div class="swym-wishlist-plus-list-ui-title">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.title}</div>
              <swym-wishlist-plus-list-ui-login-user showUser="true"></swym-wishlist-plus-list-ui-login-user>
            </div>
            <div class="swym-wishlist-plus-list-ui-container">
              <swym-wishlist-plus-list-ui-login-user showLogin="true"></swym-wishlist-plus-list-ui-login-user>
              <swym-wishlist-plus-list-ui-default-wishlist></swym-wishlist-plus-list-ui-default-wishlist>
            </div>
            <swym-wishlist-plus-list-ui-collection-list id="swym-wishlist-plus-list-ui-collection-list" class="${collectionClass}"></swym-wishlist-plus-list-ui-collection-list>
            <swym-wishlist-plus-list-ui-actions id="swym-wishlist-plus-list-ui-actions"></swym-wishlist-plus-list-ui-actions>
          </div>
        </div>
    `
    this.initUIElement()
  }

  initUIElement() {
    this.elements = {
      drawer: this.querySelector('.swym-wishlist-plus-list-ui-drawer'),
      drawerCloseButton: this.querySelector(
        '#swym-wishlist-plus-list-ui-close-drawer-button',
      ),
      drawerbackdrop: this.querySelector(
        '.swym-wishlist-plus-list-ui-backdrop',
      ),
      collectionList: this.querySelector(
        '#swym-wishlist-plus-list-ui-collection-list',
      ),
    }
    this.attachDrawerEventListner()
  }

  attachDrawerEventListner() {
    this.elements.drawerCloseButton.addEventListener(
      'click',
      this.close.bind(this),
    )
    this.elements.drawerbackdrop.addEventListener(
      'click',
      this.close.bind(this),
    )
  }

  open() {
    this.isOpen = true
    if (this.elements?.drawer) {
      this.elements.drawer.classList.remove(
        'swym-wishlist-plus-list-ui-hide-view',
      )
      document.body.classList.add('no-scroll')
    }
  }

  close() {
    this.isOpen = false
    if (this.elements?.drawer) {
      this.elements.drawer.classList.add('swym-wishlist-plus-list-ui-hide-view')
      document.body.classList.remove('no-scroll')
      history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search,
      )
    }
  }

  reRenderUI() {
    this.renderUI()
    if (this.isOpen) {
      this.open()
    }
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-as-drawer',
  SwymWishlistPlusListAsDrawer,
)

class SwymWishlistPlusListCollectionCarousel extends HTMLElement {
  constructor() {
    super()
    this.collections = []
    this.renderUI()
    this.elements = {
      carouselContainer: this.querySelector(
        '.swym-wishlist-plus-list-ui-carousel-container',
      ),
      carousel: this.querySelector(
        '#swym-wishlist-plus-list-ui-collection-carousel-items-container',
      ),
      carouselPrevButton: this.querySelector(
        '.swym-wishlist-plus-list-ui-carousel-prev-btn',
      ),
      carouselNextButton: this.querySelector(
        '.swym-wishlist-plus-list-ui-carousel-next-btn',
      ),
    }
    this.attachEventListner()
  }

  setData({ collections }) {
    this.collections = collections
    this.renderCollections()
  }

  renderUI() {
    this.innerHTML = `
    <div class="swym-wishlist-plus-list-ui-collection-carousel-container">
      <div class="swym-wishlist-plus-list-ui-collection-title">${window.WishlistPlusListUIContext.SwymWishlistPlusListStrings?.collectionTitle}</div>
      <div class="swym-wishlist-plus-list-ui-carousel-container">
        <button class="swym-wishlist-plus-list-ui-carousel-btn swym-wishlist-plus-list-ui-carousel-prev-btn" aria-label="Scroll Left">
          <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" class="swym-wishlist-plus-list-ui-scroll-button-icon" aria-hidden="true"><path d="m13.333 8 8 8-8 8" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"></path></svg>
        </button>
        <div id="swym-wishlist-plus-list-ui-collection-carousel-items-container" class="swym-wishlist-plus-list-ui-carousel">
          <swym-wishlist-plus-list-ui-loader loading="true" width="100%" height="100px"></swym-wishlist-plus-list-ui-loader>
        </div>
        <button class="swym-wishlist-plus-list-ui-carousel-btn swym-wishlist-plus-list-ui-carousel-next-btn" aria-label="Scroll Right">
          <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" class="swym-wishlist-plus-list-ui-scroll-button-icon" aria-hidden="true"><path d="m13.333 8 8 8-8 8" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"></path></svg>
        </button>
      </div>
    </div>
    `
  }

  attachEventListner() {
    this.elements.carouselPrevButton.addEventListener('click', () => {
      this.elements.carousel.scrollBy({
        left: -320,
        behavior: 'smooth',
      })
    })

    this.elements.carouselNextButton.addEventListener('click', () => {
      this.elements.carousel.scrollBy({
        left: 320,
        behavior: 'smooth',
      })
    })

    this.elements.carousel.addEventListener('scroll', () =>
      this.updateCarouselButtonVisibility(),
    )
  }

  renderCollections() {
    const collectionsContainer = this.querySelector(
      '#swym-wishlist-plus-list-ui-collection-carousel-items-container',
    )
    if (collectionsContainer) {
      if (this.collections?.length) {
        this.elements.carouselContainer.classList.add(
          'swym-wishlist-plus-list-ui-has-items',
        )
        collectionsContainer.innerHTML = this.collections
          .map((collection) => {
            const { lname, listcontents, lid } = collection

            const listImagesHtml = Array.from({ length: 3 })
              .map((_, i) => {
                const item = listcontents[i]
                return `
                  <div class="${i === 0 ? 'swym-wishlist-plus-list-ui-collection-grid-item-primary-image' : 'swym-wishlist-plus-list-ui-collection-grid-item-secondary-image'} ">
                    ${item?.iu ? `<img src="${item ? item.iu : null}" class="swym-wishlist-plus-list-ui-collection-grid-item-image" />` : `<div class="swym-wishlist-plus-list-ui-collection-grid-item-image" ></div>`}
                  </div>
                `
              })
              .join('')

            const count = listcontents.length

            return `
              <div class="swym-wishlist-plus-list-ui-collection-grid-item" data-lid="${lid}">
                <div class="swym-wishlist-plus-list-ui-collection-grid-item-image-container">
                  ${listImagesHtml}
                </div>
                <div class="swym-wishlist-plus-list-ui-collection-grid-item-name">${lname}</div>
                <div class="swym-wishlist-plus-list-ui-collection-grid-item-count">${count} ${count === 1 ? 'item' : 'items'}</div>
              </div>
            `
          })
          .join('')
        this.updateCarouselButtonVisibility()
        this.attachEventListnerToCollectionGrid()
      } else {
        this.elements.carouselContainer.classList.remove(
          'swym-wishlist-plus-list-ui-has-items',
        )
        collectionsContainer.innerHTML = `
          <div class="swym-wishlist-plus-list-ui-collection-empty">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.emptyCarouselCollectionText}</div>
        `
      }
    }
  }

  updateCarouselButtonVisibility() {
    const scrollLeft = this.elements.carousel.scrollLeft
    const maxScrollLeft =
      this.elements.carousel.scrollWidth - this.elements.carousel.clientWidth

    this.elements.carouselPrevButton.style.display =
      scrollLeft > 0 ? 'flex' : 'none'
    this.elements.carouselNextButton.style.display =
      scrollLeft < maxScrollLeft ? 'flex' : 'none'
  }

  attachEventListnerToCollectionGrid() {
    for (const collection of this.collections) {
      const collectionItem = this.elements.carousel.querySelector(
        `[data-lid="${collection.lid}"]`,
      )
      if (collectionItem) {
        collectionItem.addEventListener('click', (event) => {
          window.location.hash =
            WishlistPlusListUIContext.SwymWishlistPlusListUrls?.CollectionList +
            collection.lid
        })
      }
    }
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-collection-carousel',
  SwymWishlistPlusListCollectionCarousel,
)

class SwymWishlistPlusListWishlistItem extends HTMLElement {
  constructor() {
    super()
    this.item = null
    this.list = null
    this.selectedVariant = null
    this.collections = null
    this.cartItems = []
    this.product = {}
    this.maxProductLimit = false
    this.hasWide = false
    this.selectedVariantUnavailable = false
    this.comingSoon = false

    document.addEventListener('dna:cart:refresh', this.refreshCartItems)
  }

  setData({ item, list, collections, cartItems }) {
    this.item = item
    this.list = list
    this.collections = collections
    this.selectedVariant = this.getSelectedVariant(item)
    this.dataset.epi = this.selectedVariant.id
    this.dataset.empi = this.item?.empi
    this.dataset.du = this.item?.du
    this.cartItems = cartItems
    this.getCurrentProduct().then(() => {
      this.checkProductLimit()
      this.checkProductComingSoon()
      this.renderItem()
      this.checkProductWidthAvailability()
    })
  }

  refreshCartItems = (event) => {
    this.cartItems = event.detail.items
    this.checkProductLimit()
    this.updateButtonState()
    this.checkProductWidthAvailability()
  }

  getSelectedVariant(item) {
    const selectedVariantId = item.cprops?.selectedVariantId || item.epi
    return (
      item.productData?.variants?.find(
        (variant) => variant.id === selectedVariantId,
      ) || null
    )
  }

  getProductImage() {
    return (
      this.selectedVariant?.featured_image?.src ||
      this.item?.productData?.featured_image ||
      this.item?.iu
    )
  }

  getFormattedPrice() {
    const price = this.selectedVariant?.price || this.item?.productData?.price
    return price ? renderPrice(price) : ''
  }

  getFormattedComparePrice() {
    const comparePrice =
      this.selectedVariant?.compare_at_price ||
      this.item?.productData?.compare_at_price
    const price = this.selectedVariant?.price || this.item?.productData?.price
    if (comparePrice <= price) {
      return ''
    }
    return comparePrice ? renderPrice(comparePrice) : ''
  }

  getButtonState() {
    if (this.comingSoon) {
      return WishlistPlusListUIContext.SwymWishlistPlusListStrings?.comingSoon
    }
    if (this.selectedVariantUnavailable) {
      return WishlistPlusListUIContext.SwymWishlistPlusListStrings
        ?.notAvailableInWide
    }
    if (!this.selectedVariant?.available) {
      return WishlistPlusListUIContext.SwymWishlistPlusListStrings?.soldOut
    }

    if (this.maxProductLimit) {
      return WishlistPlusListUIContext.SwymWishlistPlusListStrings?.viewCart
    }

    return WishlistPlusListUIContext.SwymWishlistPlusListStrings?.addToCart
  }

  updateButtonState() {
    if (this.elements?.AddToCartButton) {
      const button = this.elements.AddToCartButton

      button.textContent = this.getButtonState()

      if (this.maxProductLimit) {
        button.classList.add('view-cart', 'secondary')
        button.removeAttribute('disabled')
      } else {
        button.classList.remove('view-cart')
        const disabled = this.comingSoon || !this.selectedVariant?.available

        button.classList.toggle('secondary', disabled)
        button.toggleAttribute('disabled', disabled)
      }
    }
  }

  async getCurrentProduct() {
    const productHandle = this.dataset.du.split('/products/')[1]?.split('?')[0]
    try {
      const product = await storefrontClient.getProductByHandle(productHandle)
      this.product = product || {}
    } catch (error) {
      this.product = {}
    }
  }

  checkProductLimit() {
    const productLimit =
      this.product?.properties?._limit ||
      __DNA_GLOBAL__.b2cMaxOrderQuantity ||
      0
    const itemInCart = this.cartItems.find(
      (item) => item.id.toString() === this.dataset.epi.toString(),
    )
    this.maxProductLimit = itemInCart?.quantity >= productLimit
  }

  checkProductWidthAvailability() {
    this.hasWide = this.product?.metafields?.rothys?.has_wide_color === 'true'
  }

  checkProductComingSoon() {
    this.comingSoon =
      this.product?.metafields?.rothys?.preorder_backorder_experience ===
      'coming-soon'
  }

  renderVariantSelector() {
    if (
      WishlistPlusListUIContext.WishlistPlusListConfig
        .EnableWishlistPlusListVariantSelector &&
      this.item?.productData?.variants?.length > 1
    ) {
      return `
        <div class="swym-wishlist-plus-list-ui-grid-item-variant-selector-container">
          ${this.item.productData.options
            .map(
              (option, index) => `
                ${
                  option.values && option.values.length > 1
                    ? `
                  <select option-name="${option.name}" id="variant-selector-${this.item.empi}" class="swym-wishlist-plus-list-ui-variant-selector">
                    <option value="${option.name}" disabled>${option.name}</option>
                    ${option.values
                      .map(
                        (value) =>
                          `<option value="${value}" ${
                            this.selectedVariant[`option${index + 1}`] === value
                              ? 'selected'
                              : ''
                          }>${value}</option>`,
                      )
                      .join('')}
                  </select>
                `
                    : ''
                }
              `,
            )
            .join('')}
        </div>
      `
    }
    return ''
  }

  renderOptionButton() {
    //let canEdit = this.list?.userinfo?.em === WishlistPlusListUIContext.SwymWlCustomerData?.email || this.list?.canEdit || !this.list?.userinfo;
    if (WishlistPlusListUIContext.checkUserCanEditList(this.list)) {
      return `
        <button id="swym-wishlist-plus-list-ui-grid-item-option-button" class="swym-wishlist-plus-list-ui-grid-item-option-button">
          <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" fill="currentcolor" height="15px" width="15px" version="1.1" id="Capa_1" viewBox="0 0 32.055 32.055" xml:space="preserve">
            <g>
              <path d="M3.968,12.061C1.775,12.061,0,13.835,0,16.027c0,2.192,1.773,3.967,3.968,3.967c2.189,0,3.966-1.772,3.966-3.967   C7.934,13.835,6.157,12.061,3.968,12.061z M16.233,12.061c-2.188,0-3.968,1.773-3.968,3.965c0,2.192,1.778,3.967,3.968,3.967   s3.97-1.772,3.97-3.967C20.201,13.835,18.423,12.061,16.233,12.061z M28.09,12.061c-2.192,0-3.969,1.774-3.969,3.967   c0,2.19,1.774,3.965,3.969,3.965c2.188,0,3.965-1.772,3.965-3.965S30.278,12.061,28.09,12.061z"/>
            </g>
          </svg>
        </button>
      `
    }
    return ''
  }

  renderItem() {
    let localizedUrl = this.item.du
    const locale = window.Shopify.routes.root
    if (locale !== '/') {
      const splitUrl = this.item.du.split('/products/')
      localizedUrl = `${splitUrl[0]}${locale}products/${splitUrl[1]}`
    }
    this.innerHTML = `
      <div class="swym-wishlist-plus-list-ui-grid-item">
        <a href="${localizedUrl}" class="swym-wishlist-plus-list-ui-grid-item-image-container">
          <img src="${this.getProductImage()}" class="swym-wishlist-plus-list-ui-grid-item-image" />
        </a>
        <div class="swym-wishlist-plus-list-ui-grid-item-content">
          <a href="${localizedUrl}" class="swym-wishlist-plus-list-ui-grid-item-title-link">
           <div class="swym-wishlist-plus-list-ui-grid-item-title">${this.item?.dt || ''}</div>
          </a>
          <div class="swym-wishlist-plus-list-ui-grid-item-price">
          <span class="swym-wishlist-plus-list-ui-grid-item-compare-price">${this.getFormattedComparePrice()}</span>
          <span class="swym-wishlist-plus-list-ui-grid-item-final-price ${this.getFormattedComparePrice() ? 'markdown' : ''}">
            ${this.getFormattedPrice()}
          </span>
          </div>
          ${this.renderVariantSelector()}
          <div class="swym-wishlist-plus-list-ui-grid-item-action-container">
            <button class="swym-wishlist-plus-list-ui-grid-item-add-to-cart-button"
              data-action="add-to-cart">
              ${this.getButtonState()}
            </button>
            ${this.renderOptionButton()}
          </div>
        </div>
      </div>
    `

    this.initItemElements()
    this.updateButtonState()
    this.addEventListeners()
  }

  initItemElements() {
    this.elements = {
      AddToCartButton: this.querySelector('[data-action="add-to-cart"]'),
      Image: this.querySelector('.swym-wishlist-plus-list-ui-grid-item-image'),
      Price: this.querySelector(
        '.swym-wishlist-plus-list-ui-grid-item-final-price',
      ),
      ComparePrice: this.querySelector(
        '.swym-wishlist-plus-list-ui-grid-item-compare-price',
      ),
      OptionButton: this.querySelector(
        '#swym-wishlist-plus-list-ui-grid-item-option-button',
      ),
    }
  }

  addEventListeners() {
    this.elements.AddToCartButton?.addEventListener('click', (event) => {
      if (event.target.classList.contains('view-cart')) {
        document.dispatchEvent(new CustomEvent('dna:cart:open'))
      } else {
        this.handleAddToCart(event)
      }
    })
    this.elements.OptionButton?.addEventListener('click', () => {
      WishlistPlusListUIExtensions.SwymWishlistPlusListActions?.setData({
        listId: this.list.lid,
        list: this.list,
        item: this.item,
        selectedVariantId: this.selectedVariant?.id,
        collections: this.collections,
      })
      WishlistPlusListUIExtensions.SwymWishlistPlusListActions?.openDrawer()
    })

    const variantSelectors = this.querySelectorAll(
      '.swym-wishlist-plus-list-ui-variant-selector',
    )
    if (variantSelectors) {
      for (const selector of variantSelectors) {
        selector.addEventListener('change', () =>
          this.handleVariantSelectionChange(),
        )
      }
    }
  }

  handleAddToCart = async (event) => {
    event.preventDefault()
    const button = event.target

    const { epi, du } = this.dataset
    const variant = this.product.variants.find((variant) => variant.id === epi)
    const properties = getLineItemProperties(this.product, variant)

    try {
      await addToCart({ id: epi, quantity: 1, properties }, false)
      this.checkProductLimit()
      if (this.maxProductLimit) {
        button.innerHTML =
          WishlistPlusListUIContext.SwymWishlistPlusListStrings?.viewCart
        button.classList.add('view-cart')
        button.classList.add('secondary')
      } else {
        button.innerHTML =
          WishlistPlusListUIContext.SwymWishlistPlusListStrings?.addToCart
      }

      // @segment
      Segment.protect(() => {
        const listProduct = this.list.listcontents.find((item) => {
          return item.epi.toString() === variant.id
        })

        Segment.track(Segment.CONSTANTS.EVENTS.WISHLIST_PRODUCT_ATC, {
          brand: Segment.CONSTANTS.BRAND,
          category: splitProductName(this.product.title).style,
          image_url: this.product.media[0]?.url,
          name: splitProductName(this.product.title).color,
          presentment_price: Number(minorToMajorUnit(variant.price)),
          price: listProduct?.pr,
          product_id: this.product.id,
          quantity: 1,
          shopify_product_id: this.product.id,
          shopify_variant_id: variant.id,
          sku: variant.sku,
          sku_short: getMasterSku(variant.sku),
          url: `${window.location.origin}${Shopify.routes.root}products/${this.product.handle}`,
          variant: variant.title,
          wishlist_id: this.list.lid,
          wishlist_count: this.list.cnt,
          wishlist_name: this.list.lname,
          wishlist_url: `${window.location.origin}${Shopify.routes.root}pages/custom-wishlist#swym-list`,
          wishlist_products: this.list.listcontents.map((item) => {
            return {
              brand: Segment.CONSTANTS.BRAND,
              category: splitProductName(item.dt).style,
              currency: 'USD',
              image_url: item.iu,
              name: splitProductName(item.dt).color,
              presentment_currency: Shopify?.currency?.active,
              presentment_price: Number(
                minorToMajorUnit(item.productData.price),
              ),
              price: item.pr,
              product_id: item.empi?.toString(),
              quantity: 1,
              shopify_product_id: item.empi?.toString(),
              shopify_variant_id: item.epi?.toString(),
              sku: item.sku,
              sku_short: getMasterSku(item.sku),
              url: item.du,
              variant: item.vi,
            }
          }),
        })
      })
    } catch (error) {
      console.error('Error occurred:', error.message)
    }

    WishlistPlusListUIExtensions.SwymWishlistPlusListNotification?.setMessage({
      message:
        WishlistPlusListUIContext.SwymWishlistPlusListStrings
          ?.notificationMessageAddedToCart,
      status:
        WishlistPlusListUIContext.SwymWishlistPlusListNotificationStatusTypes
          ?.Toast,
      iconType:
        WishlistPlusListUIContext.SwymWishlistPlusListNotificationIconType
          ?.SuccessIcon,
      duration: 5000,
      action: {
        label:
          WishlistPlusListUIContext.SwymWishlistPlusListStrings
            ?.notificationActionGoToCart,
      },
    })
    document.dispatchEvent(new CustomEvent('dna:cart:open'))
  }

  async handleVariantSelectionChange() {
    const selectedOptions = []
    const variantSelectors = this.querySelectorAll(
      '.swym-wishlist-plus-list-ui-variant-selector',
    )
    variantSelectors.forEach((selector, index) => {
      const selectedValue = selector.value
      if (selectedValue) {
        selectedOptions.push(selectedValue)
      }
    })

    if (selectedOptions.includes('Wide') && !this.hasWide) {
      this.selectedVariantUnavailable = true
      this.elements.AddToCartButton.setAttribute('disabled', true)
      this.elements.AddToCartButton.classList.add('secondary')
      this.elements.AddToCartButton.textContent = this.getButtonState()
      return
    }

    const selectedVariant = this.item.productData.variants.find((variant) =>
      selectedOptions.every(
        (value, index) => variant[`option${index + 1}`] === value,
      ),
    )

    this.selectedVariant = selectedVariant
    this.selectedVariantUnavailable = false
    this.dataset.epi = selectedVariant?.id
    this.checkProductLimit()

    if (selectedVariant) {
      const disabled = this.comingSoon || !selectedVariant.available
      this.elements.AddToCartButton.textContent = this.getButtonState()
      if (this.maxProductLimit) {
        this.elements.AddToCartButton.removeAttribute('disabled')
        this.elements.AddToCartButton.classList.add('view-cart', 'secondary')
      } else {
        this.elements.AddToCartButton.classList.remove('view-cart')
        this.elements.AddToCartButton.toggleAttribute('disabled', disabled)
        this.elements.AddToCartButton.classList.toggle('secondary', disabled)
      }
      this.elements.Price.textContent = this.getFormattedPrice()
      this.elements.ComparePrice.textContent = this.getFormattedComparePrice()

      const productImage =
        selectedVariant?.featured_image?.src ||
        this.item.productData?.featured_image ||
        item.iu
      this.elements.Image.src = productImage

      const canEdit =
        this.list?.userinfo?.em ===
          WishlistPlusListUIContext.SwymWlCustomerData?.email ||
        this.list?.canEdit

      if (WishlistPlusListUIContext.checkUserCanEditList(this.list)) {
        const { epi, empi, du } = this.item
        await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.updateProductFromList(
          { epi, empi, du, cprops: { selectedVariantId: selectedVariant.id } },
          this.list.lid,
        )
      }
    } else {
      console.error(
        'No matching variant found for the selected options:',
        selectedOptions,
      )
    }
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-item',
  SwymWishlistPlusListWishlistItem,
)

class SwymWishlistPlusListDefaultList extends HTMLElement {
  constructor() {
    super()
    this.wishlist = null
    this.collections = null
    this.renderUI()
    this.elements = {
      itemsContainer: this.querySelector(
        '#swym-wishlist-plus-list-ui-items-container',
      ),
    }
    this.cartItems = []
  }

  async fetchCartData() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`)
      if (!response.ok) {
        throw new Error('Error fetching cart')
      }
      const data = await response.json()
      this.cartItems = data.items
      return data.items
    } catch (error) {
      console.error('Error fetching cart data:', error)
    }
  }

  renderUI() {
    this.innerHTML = `
      <div class="swym-wishlist-plus-list-ui-default-list-container">
        <div id="swym-wishlist-plus-list-ui-items-container" class="swym-wishlist-plus-list-ui-items-container">
          <swym-wishlist-plus-list-ui-loader loading="true" height="100px"></swym-wishlist-plus-list-ui-loader>
        </div>
      </div>
    `
  }

  setData({ wishlist, collections }) {
    this.wishlist = wishlist
    this.collections = collections
    this.elements.itemsContainer.dataset.lid = this.wishlist.lid
    this.renderWishlist()
  }

  async renderWishlist() {
    const cartItems = await this.fetchCartData()
    if (this.wishlist?.listcontents?.length && this.elements.itemsContainer) {
      const wishlistedProducts = await this.fetchProductData(
        this.wishlist.listcontents,
      )
      const fragment = document.createDocumentFragment()

      for (const item of wishlistedProducts) {
        const wishlistItem = document.createElement('template')
        wishlistItem.innerHTML = `<swym-wishlist-plus-list-ui-item id="swym-wishlist-plus-list-ui-item-${item.empi}-${item.epi}" ></swym-wishlist-plus-list-ui-item>`
        fragment.appendChild(wishlistItem.content)
      }

      this.elements.itemsContainer.innerHTML = ''
      this.elements.itemsContainer.appendChild(fragment)
      for (const item of wishlistedProducts) {
        const wishlistItemElement = document.querySelector(
          `#swym-wishlist-plus-list-ui-item-${item.empi}-${item.epi}`,
        )
        if (wishlistItemElement) {
          wishlistItemElement.setData({
            item,
            list: this.wishlist,
            collections: this.collections,
            cartItems: cartItems,
          })
        }
      }
    } else {
      this.elements.itemsContainer.innerHTML = `
        <div class="swym-wishlist-plus-list-ui-empty-container">
          <div class="swym-wishlist-plus-list-ui-empty-wishlist-container">
            <div class="swym-wishlist-plus-list-ui-empty-wishlist-content">
              <div class="swym-wishlist-plus-list-ui-empty-wishlist-title">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.emptyWishlistTitle}</div>
              <div class="swym-wishlist-plus-list-ui-empty-wishlist-description">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.emptyWishlistDescription}</div>
            </div>
          </div>
        </div>
      `
    }
  }

  async fetchProductData(wishlist) {
    const productDataPromises = wishlist.map(async (listItem) => {
      if (
        WishlistPlusListUIContext.SwymWishlistPlusListShopifyProductData?.[
          listItem.epi
        ]
      ) {
        listItem.productData =
          WishlistPlusListUIContext.SwymWishlistPlusListShopifyProductData[
            listItem.epi
          ]
      } else {
        const response = await fetch(`${listItem.du.split('?')[0]}.js`)
        if (!response.ok) {
          return null
        }
        listItem.productData = await response.json()
        WishlistPlusListUIContext.SwymWishlistPlusListShopifyProductData[
          listItem.epi
        ] = listItem.productData
      }
      return listItem
    })
    const results = await Promise.all(productDataPromises)
    return results.filter((item) => item !== null)
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-default-wishlist',
  SwymWishlistPlusListDefaultList,
)

class SwymWishlistPlusListCollectionList extends HTMLElement {
  constructor() {
    super()
    this.collectionList = null
    this.collections = null
    this.renderUI()
    this.elements = {
      backButton: this.querySelector(
        '.swym-wishlist-plus-list-ui-collection-list-back-button',
      ),
      itemsContainer: this.querySelector(
        '#swym-wishlist-plus-list-ui-collection-list-items-container',
      ),
      title: this.querySelector('.swym-wishlist-plus-list-ui-title'),
      swymListActions: this.querySelector(
        'swym-wishlist-plus-list-ui-collection-list-action',
      ),
      userAccessInfo: this.querySelector(
        '#swym-wishlist-plus-list-ui-collection-list-access-info',
      ),
    }
    this.addEventListenerToView()
  }

  setData({ list, collections }) {
    this.collectionList = list
    this.collections = collections
    if (this.collectionList) {
      this.elements.itemsContainer.dataset.lid = this.collectionList.lid
      this.elements.swymListActions.setData({ list, collections })

      this.renderCollectionlist()
    }
  }

  async fetchCartData() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`)
      if (!response.ok) {
        throw new Error('Error fetching cart')
      }
      const data = await response.json()
      this.cartItems = data.items
      return data.items
    } catch (error) {
      console.error('Error fetching cart data:', error)
    }
  }

  renderUI() {
    this.innerHTML = `
    <div class="swym-wishlist-plus-list-ui-collection-list-container">
      <div class="swym-wishlist-plus-list-ui-collection-list-header">
        <div class="swym-wishlist-plus-list-ui-collection-list-back-button">
          <svg data-v-bfb713a3="" data-v-326364ff="" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="base-icon back-icon" aria-hidden="true"><path d="M15.44 7a.96.96 0 0 0-.96-.96H3.951l3.903-3.638A.96.96 0 1 0 6.544 1L.856 6.297a.963.963 0 0 0 0 1.404L6.543 13a.96.96 0 1 0 1.31-1.404L3.952 7.96H14.48a.96.96 0 0 0 .96-.96Z" fill="currentColor"></path></svg>
        </div>
        <div class="swym-wishlist-plus-list-ui-title">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.wishlistTitle}</div>
      </div>
      <div class="swym-wishlist-plus-list-ui-collection-list-content">
        <swym-wishlist-plus-list-ui-login-user showLogin="true"></swym-wishlist-plus-list-ui-login-user>
        <swym-wishlist-plus-list-ui-collection-list-action></swym-wishlist-plus-list-ui-collection-list-action>
        <div id="swym-wishlist-plus-list-ui-collection-list-access-info"></div>
        <div id="swym-wishlist-plus-list-ui-collection-list-items-container" class="swym-wishlist-plus-list-ui-items-container">
          <swym-wishlist-plus-list-ui-loader loading="true" height="80%"></swym-wishlist-plus-list-ui-loader>
        </div>
      </div>
    </div>
    `
  }

  addEventListenerToView() {
    this.elements.backButton.addEventListener('click', () => {
      window.location.hash =
        WishlistPlusListUIContext.SwymWishlistPlusListUrls?.List
      this.closeCollectinListView()
    })
  }

  async renderCollectionlist() {
    const cartItems = await this.fetchCartData()

    if (this.collectionList?.listcontents && this.elements.itemsContainer) {
      this.elements.title.innerHTML = `<div class="swym-wishlist-plus-list-ui-action-title">${this.collectionList.lname}</div>`

      const wishlistedProducts = await this.fetchProductData(
        this.collectionList.listcontents,
      )
      const fragment = document.createDocumentFragment()

      for (const item of wishlistedProducts) {
        const wishlistItem = document.createElement('template')
        wishlistItem.innerHTML = `<swym-wishlist-plus-list-ui-item id="swym-wishlist-plus-list-ui-collection-item-${item.empi}-${item.epi}" ></swym-wishlist-plus-list-ui-item>`
        fragment.appendChild(wishlistItem.content)
      }
      this.elements.itemsContainer.innerHTML = ''
      this.elements.itemsContainer.appendChild(fragment)

      for (const item of wishlistedProducts) {
        const wishlistItemElement = document.querySelector(
          `#swym-wishlist-plus-list-ui-collection-item-${item.empi}-${item.epi}`,
        )
        if (wishlistItemElement) {
          wishlistItemElement.setData({
            item,
            list: this.collectionList,
            collections: this.collections,
            cartItems: cartItems,
          })
        }
      }

      const isUserLogged = !!window.swymCustomerId
      if (
        isUserLogged &&
        !!this.collectionList?.userinfo?.em &&
        this.collectionList?.userinfo?.em !==
          WishlistPlusListUIContext.SwymWlCustomerData?.email
      ) {
        const email = this.collectionList?.userinfo?.em
        this.elements.userAccessInfo.innerHTML =
          WishlistPlusListUIContext.SwymWishlistPlusListRenderStringTemplate(
            WishlistPlusListUIContext.SwymWishlistPlusListStrings
              ?.sharedCollectionMessage,
            { email },
          )
      } else {
        this.elements.userAccessInfo.innerHTML = ''
      }

      this.openCollectionListView()
    }
  }

  async fetchProductData(wishlist) {
    const productDataPromises = wishlist.map(async (listItem) => {
      if (
        WishlistPlusListUIContext.SwymWishlistPlusListShopifyProductData?.[
          listItem.epi
        ]
      ) {
        listItem.productData =
          WishlistPlusListUIContext.SwymWishlistPlusListShopifyProductData[
            listItem.epi
          ]
      } else {
        const response = await fetch(`${listItem.du.split('?')[0]}.js`)
        if (!response.ok) {
          return null
        }
        listItem.productData = await response.json()
        WishlistPlusListUIContext.SwymWishlistPlusListShopifyProductData[
          listItem.epi
        ] = listItem.productData
      }
      return listItem
    })
    const results = await Promise.all(productDataPromises)
    return results.filter((item) => item !== null)
  }

  openCollectionListView() {
    this.classList.add('swym-wishlist-plus-list-ui-show-collection-list')
  }

  closeCollectinListView() {
    this.classList.remove('swym-wishlist-plus-list-ui-show-collection-list')
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-collection-list',
  SwymWishlistPlusListCollectionList,
)

class SwymWishlistPlusCollectionListAction extends HTMLElement {
  constructor() {
    super()
    this.wishlistItem = null
    this.collections = null
  }

  setData({ list, collections }) {
    this.collectionList = list
    this.collections = collections

    this.render()
  }

  render() {
    const isUserLogged = !!window.swymCustomerId
    //let canEdit = ( this.collectionList?.userinfo?.em === WishlistPlusListUIContext.SwymWlCustomerData?.email) || this.collectionList?.canEdit || !this.collectionList?.userinfo;
    const canEdit = WishlistPlusListUIContext.checkUserCanEditList(
      this.collectionList,
    )
    this.innerHTML = `
      ${
        isUserLogged
          ? `
        <div class="swym-wishlist-plus-list-ui-collection-list-action-container">
          ${
            canEdit
              ? `
            <button class="swym-wishlist-plus-list-ui-collection-list-action-share-button">
              <svg data-v-bfb713a3="" data-v-5fcf9ed0="" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" class="base-icon share-icon" aria-hidden="true"><path d="M8.6 1.7a.225.225 0 0 0-.243-.03.231.231 0 0 0-.133.206v3.116c-.843-.039-3.583.007-5.59 1.94-1.637 1.576-2.357 4-2.14 7.205a.23.23 0 0 0 .229.214c.086 0 .166-.05.205-.13.023-.05 2.384-4.845 7.296-4.242v3.155c0 .087.053.17.133.205a.224.224 0 0 0 .242-.03l6.86-5.627a.229.229 0 0 0 0-.355l-6.86-5.626Z" fill="currentColor"></path></svg>
              ${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.shareCollectionCta}
            </button>
            <button class="swym-wishlist-plus-list-ui-collection-list-action-edit-button">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.editCollectionCta}</button>
          `
              : `
          <button class="swym-wishlist-plus-list-ui-collection-list-action-save-button">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.saveCollectionCta}</button>
          `
          }
        </div>
      `
          : ''
      }
    `

    this.attachListeners()
  }

  attachListeners() {
    const editButton = this.querySelector(
      '.swym-wishlist-plus-list-ui-collection-list-action-edit-button',
    )
    const shareButton = this.querySelector(
      '.swym-wishlist-plus-list-ui-collection-list-action-share-button',
    )
    const saveCollectionButton = this.querySelector(
      '.swym-wishlist-plus-list-ui-collection-list-action-save-button',
    )

    if (editButton) {
      editButton.addEventListener('click', () => this.handleEdit())
    }

    if (shareButton) {
      shareButton.addEventListener('click', () => this.handleShare())
    }

    if (saveCollectionButton) {
      saveCollectionButton.addEventListener('click', () =>
        this.handleSaveCollection(),
      )
    }
  }

  async handleEdit() {
    WishlistPlusListUIExtensions.SwymWishlistPlusListActions?.setData({
      listId: this.collectionList.lid,
      list: this.collectionList,
      collections: this.collections,
      actionType:
        WishlistPlusListUIContext.SwymWishlistPlusListActionTypes
          .EditCollection,
    })
    WishlistPlusListUIExtensions.SwymWishlistPlusListActions?.openDrawer()
  }

  async handleShare() {
    if (navigator.share) {
      try {
        const link =
          await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.generateSharedListURL(
            this.collectionList.lid,
          )
        const shareUrl = `${window.location.origin}/${WishlistPlusListUIContext.SwymWishlistPlusListUrls?.CollectionList}${this.collectionList.lid}`
        await navigator.share({
          title:
            WishlistPlusListUIContext.SwymWishlistPlusListStrings
              ?.shareCollectionTitle,
          text: WishlistPlusListUIContext.SwymWishlistPlusListStrings
            ?.shareCollectionMessage,
          url: shareUrl,
        })

        // @segment
        Segment.protect(() => {
          Segment.track(Segment.CONSTANTS.EVENTS.WISHLIST_SHARED, {
            wishlist_products: this.collectionList.listcontents.map((item) => {
              return {
                brand: Segment.CONSTANTS.BRAND,
                category: splitProductName(item.dt).style,
                currency: 'USD',
                image_url: item.iu,
                name: splitProductName(item.dt).color,
                presentment_currency: Shopify?.currency?.active,
                presentment_price: Number(
                  minorToMajorUnit(item.productData.price),
                ),
                price: item.pr,
                product_id: item.empi?.toString(),
                quantity: 1,
                shopify_product_id: item.empi?.toString(),
                shopify_variant_id: item.epi?.toString(),
                sku: item.sku,
                sku_short: getMasterSku(item.sku),
                url: item.du,
                variant: item.vi,
              }
            }),
            wishlist_id: this.collectionList.lid,
            wishlist_count: this.collectionList.cnt,
            wishlist_name: this.collectionList.lname,
            wishlist_url: shareUrl,
          })
        })
      } catch (error) {
        console.warn('Failed to sharing content:', error)
      }
    } else {
      console.warn('Your browser does not support the Web Share API.')
    }
  }

  async handleSaveCollection() {
    const savedList =
      await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.createList(
        this.collectionList.lname,
      )
    const itemsToAdd = this.collectionList.listcontents.map(
      ({ empi, epi, du }) => ({ empi, epi, du }),
    )
    await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.addProductsToList(
      itemsToAdd,
      savedList.lid,
    )
    window.location.hash =
      WishlistPlusListUIContext.SwymWishlistPlusListUrls?.List

    WishlistPlusListUIExtensions.SwymWishlistPlusListNotification?.setMessage({
      message:
        WishlistPlusListUIContext.SwymWishlistPlusListStrings
          ?.notificationMessageCollectionSaved,
      status:
        WishlistPlusListUIContext.SwymWishlistPlusListNotificationStatusTypes
          ?.Toast,
      iconType:
        WishlistPlusListUIContext.SwymWishlistPlusListNotificationIconType
          ?.SuccessIcon,
      duration: 5000,
      action: {
        label:
          WishlistPlusListUIContext.SwymWishlistPlusListStrings
            ?.notificationActionView,
        onClick: () => {
          window.location.hash =
            WishlistPlusListUIContext.SwymWishlistPlusListUrls?.CollectionList +
            savedList.lid
        },
      },
    })
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-collection-list-action',
  SwymWishlistPlusCollectionListAction,
)

class SwymWishlistPlusListActions extends HTMLElement {
  constructor() {
    super()

    this.actionType =
      WishlistPlusListUIContext.SwymWishlistPlusListActionTypes.ManageListItem

    this.wishlistItem = null
    this.collections = null
    this.listId = null
    this.list = null
    this.renderUI()
    this.elements = {
      container: this.querySelector(
        '.swym-wishlist-plus-list-ui-actions-container',
      ),
      backdrop: this.querySelector(
        '.swym-wishlist-plus-list-ui-action-backdrop',
      ),
      closeButton: this.querySelector(
        '.swym-wishlist-plus-list-ui-action-close-button',
      ),
      title: this.querySelector(
        '.swym-wishlist-plus-list-ui-action-title-container',
      ),
      optionsContainer: this.querySelector(
        '#swym-wishlist-plus-list-ui-action-options',
      ),
      wishlistItemsContainer: document.querySelector(
        '#swym-wishlist-plus-list-ui-items-container',
      ),
    }
    this.addEventListenerToView()
  }

  renderUI() {
    this.innerHTML = `
    <div class="swym-wishlist-plus-list-ui-actions-container">
      <div class="swym-wishlist-plus-list-ui-action-backdrop"></div>
      <div class="swym-wishlist-plus-list-ui-action-layout">
        <div class="swym-wishlist-plus-list-ui-action-header">
          <div class="swym-wishlist-plus-list-ui-action-title-container"></div>
          <button class="swym-wishlist-plus-list-ui-action-close-button">
            <svg data-v-bfb713a3="" data-v-e057eb37="" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" class="base-icon close-icon" aria-hidden="true"><path d="m10.536 1.5-9 9M1.536 1.5l9 9" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path></svg>
          </button>
        </div>
        <div id="swym-wishlist-plus-list-ui-action-options"></div>
      </div>
    </div>
    `
  }

  setData({
    listId,
    list,
    item,
    selectedVariantId,
    collections,
    actionType = WishlistPlusListUIContext.SwymWishlistPlusListActionTypes
      .ManageListItem,
  }) {
    this.listId = listId
    this.list = list
    this.wishlistItem = item
    this.collections = WishlistPlusListUIContext.WishlistListCollections
    this.currentSlectedVariantId = selectedVariantId
    this.elements.wishlistItemsContainer = document.querySelector(
      `.swym-wishlist-plus-list-ui-items-container[data-lid="${listId}"]`,
    )
    if (
      actionType ===
      WishlistPlusListUIContext.SwymWishlistPlusListActionTypes.ManageListItem
    ) {
      this.renderWishlistItemOptions()
    } else if (
      actionType ===
      WishlistPlusListUIContext.SwymWishlistPlusListActionTypes.AddToCollection
    ) {
      this.renderAddToCollection()
    } else if (
      actionType ===
      WishlistPlusListUIContext.SwymWishlistPlusListActionTypes.CreateCollection
    ) {
      this.renderCreateCollection()
    } else if (
      actionType ===
      WishlistPlusListUIContext.SwymWishlistPlusListActionTypes.EditCollection
    ) {
      this.renderEditCollection()
    }
  }

  getSelectedVariant(item) {
    const selectedVariantId = this.currentSlectedVariantId || item.epi
    return item.productData.variants.find(
      (variant) => variant.id === selectedVariantId,
    )
  }

  async renderWishlistItemOptions() {
    if (this.wishlistItem && this.elements.optionsContainer) {
      const selectedVariant = this.getSelectedVariant(this.wishlistItem)
      const productImage =
        selectedVariant?.featured_image?.src ||
        this.wishlistItem.productData?.featured_image ||
        this.wishlistItem.iu
      this.elements.title.innerHTML = `
        ${this.wishlistItem?.iu ? `<img src="${productImage}" class="swym-wishlist-plus-list-ui-action-title-image" />` : ''}
        <div class="swym-wishlist-plus-list-ui-action-title">${this.wishlistItem.dt}</div>
      `
      //let canEdit = (this.list?.userinfo?.em === WishlistPlusListUIContext.SwymWlCustomerData?.email) || this.list?.canEdit;
      const canEdit = WishlistPlusListUIContext.checkUserCanEditList(this.list)
      this.elements.optionsContainer.innerHTML = `
        ${canEdit ? `<div class="swym-wishlist-plus-list-ui-action-btn remove-from-list-button">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.removeItemCta}</div>` : ''}
      `
      this.addOptionEventListeners()
    }
  }

  addOptionEventListeners() {
    const addToCollectionBtn = this.querySelector('.add-to-collection-button')
    const removeFromListBtn = this.querySelector('.remove-from-list-button')

    addToCollectionBtn?.addEventListener('click', () => {
      this.renderAddToCollection()
    })

    removeFromListBtn?.addEventListener('click', async () => {
      try {
        const { epi, empi, du } = this.wishlistItem

        await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.removeProductFromList(
          { epi, empi, du },
          this.listId,
        )

        const elementDeleted =
          this.elements.wishlistItemsContainer?.querySelector(
            `[data-epi="${epi}"]`,
          )
        elementDeleted?.remove()
        this.closeDrawer()
      } catch (error) {
        console.warn('Failed to remove product from the list:', error)
      }
    })
  }

  async renderAddToCollection() {
    this.collections = WishlistPlusListUIContext.WishlistListCollections || []
    if (this.collections && this.elements.optionsContainer) {
      this.elements.title.innerHTML = `
        <div class="swym-wishlist-plus-list-ui-action-title">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.addToCollectionTitle}</div>
      `
      this.elements.optionsContainer.innerHTML = `
        <div class="swym-wishlist-plus-list-ui-collection-action-btn create-new-collection-button">
            <div class="swym-wishlist-plus-list-ui-action-collection-image-container">
              <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="swym-wishlist-plus-list-ui-action-collection-item-add-icon" aria-hidden="true"><path d="M18.096 12H5.974M12.035 5.94v12.12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            </div>
            <div class="swym-wishlist-plus-list-ui-action-collection-item-name">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.createCollectionCta}</div>
        </div>
        ${this.collections
          .map((collection) => {
            const { lid, lname, listcontents } = collection
            const item = listcontents[0]
            const count = listcontents.length

            return `
            <div class="swym-wishlist-plus-list-ui-collection-action-btn add-item-to-collection swym-wishlist-plus-list-ui-action-collection-item-container" data-lid="${lid}">
              <div class="swym-wishlist-plus-list-ui-action-collection-image-container">
                ${item?.iu ? `<img src="${item ? item.iu : null}" class="swym-wishlist-plus-list-ui-action-collection-item-image" />` : `<div class="swym-wishlist-plus-list-ui-action-collection-item-image" ></div>`}
              </div>
              <div class="swym-wishlist-plus-list-ui-action-collection-content">
                <div class="swym-wishlist-plus-list-ui-action-collection-item-name">${lname}</div>
                <div class="swym-wishlist-plus-list-ui-action-collection-item-count">${count} ${count === 1 ? 'item' : 'items'}</div>
              </div>
            </div>
          `
          })
          .join('')}
      `
      this.addMangeCollectionEventListeners()
    }
  }

  addMangeCollectionEventListeners() {
    const createCollectionBtn = this.querySelector(
      '.create-new-collection-button',
    )

    createCollectionBtn?.addEventListener('click', () => {
      this.renderCreateCollection()
    })

    for (const collection of this.collections) {
      const collectionItem = this.elements.optionsContainer.querySelector(
        `[data-lid="${collection.lid}"]`,
      )
      if (collectionItem) {
        collectionItem.addEventListener('click', async (event) => {
          try {
            let { epi, empi, du, iu } = this.wishlistItem
            if (this.currentSlectedVariantId) {
              epi = this.currentSlectedVariantId
            }
            await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.addProductsToList(
              [{ empi, epi, du }],
              collection.lid,
            )
            this.closeDrawer()
            WishlistPlusListUIExtensions.SwymWishlistPlusListNotification?.setMessage(
              {
                message:
                  WishlistPlusListUIContext.SwymWishlistPlusListStrings
                    ?.notificationMessageAddedToCollection,
                status:
                  WishlistPlusListUIContext
                    .SwymWishlistPlusListNotificationStatusTypes?.Toast,
                duration: 5000,
                image: iu,
                action: {
                  label:
                    WishlistPlusListUIContext.SwymWishlistPlusListStrings
                      ?.notificationActionView,
                  onClick: () => {
                    window.location.hash =
                      WishlistPlusListUIContext.SwymWishlistPlusListUrls
                        ?.CollectionList + collection.lid
                  },
                },
              },
            )
          } catch (error) {
            console.warn('Error adding product to list', error)
          }
        })
      }
    }
  }

  renderCreateCollection() {
    if (this.elements.optionsContainer) {
      this.elements.title.innerHTML = `<div class="swym-wishlist-plus-list-ui-action-title">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.createCollectionCta}</div>`
      this.elements.optionsContainer.innerHTML = `
        <form id="swym-wishlist-plus-list-ui-create-collection-form">
          <div class="swym-wishlist-plus-list-ui-create-collection-name-form-group">
            <input
              type="text"
              id="swym-wishlist-plus-list-ui-create-collection-name-input"
              class="swym-wishlist-plus-list-ui-action-input create-collection-name-input"
              placeholder=""
            />
            <label for="swym-wishlist-plus-list-ui-create-collection-name-input" class="swym-wishlist-plus-list-ui-create-collection-name-label">Name</label>
            <div id="swym-wishlist-plus-list-ui-create-collection-error-message-container" class="error-message" style="display: none;">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="base-icon error-icon" aria-hidden="true"><path d="M7 .7a6.3 6.3 0 1 0 0 12.6A6.3 6.3 0 0 0 7 .7Zm.7 9.8H6.3V9.1h1.4v1.4Zm0-2.1H6.3V3.5h1.4v4.9Z" fill="currentColor"></path></svg>
              <span id="swym-wishlist-plus-list-ui-create-collection-error-message">Name is required</span>
            </div>
          </div>
          <button type="submit" id="swym-wishlist-plus-list-ui-save-collection-list-button" class="swym-wishlist-plus-list-ui-save-collection-list-button" disabled>${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.saveNewCollectionCta}</button>
        </form>
      `
      this.addCreateCollectionEventListeners()
    }
  }

  addCreateCollectionEventListeners() {
    const createCollectionForm = this.querySelector(
      '#swym-wishlist-plus-list-ui-create-collection-form',
    )
    const collectionNameInput = this.querySelector(
      '#swym-wishlist-plus-list-ui-create-collection-name-input',
    )
    const collectionSaveButton = this.querySelector(
      '#swym-wishlist-plus-list-ui-save-collection-list-button',
    )
    const errorMessageContainer = this.querySelector(
      '#swym-wishlist-plus-list-ui-create-collection-error-message-container',
    )
    const errorMessage = this.querySelector(
      '#swym-wishlist-plus-list-ui-create-collection-error-message',
    )

    collectionNameInput.addEventListener('input', () => {
      const name = collectionNameInput.value.trim()
      let error

      const isNameExist = this.collections.some(
        (collection) => collection.lname === name,
      )

      if (name === '') {
        error =
          WishlistPlusListUIContext.SwymWishlistPlusListStrings
            ?.errorMessageListNameRequired
      } else if (name.length < 3) {
        error =
          WishlistPlusListUIContext.SwymWishlistPlusListStrings
            ?.errorMessageListNameRequire3Char
      } else if (isNameExist) {
        error =
          WishlistPlusListUIContext.SwymWishlistPlusListStrings
            ?.errorMessageListNameAlreadyExist
      }

      if (name.length > 0) {
        collectionNameInput.classList.add(
          'swym-wishlist-plus-list-ui-input-has-value',
        )
      } else {
        collectionNameInput.classList.remove(
          'swym-wishlist-plus-list-ui-input-has-value',
        )
      }

      if (error) {
        errorMessage.innerHTML = error
        errorMessageContainer.style.display = 'flex'
        collectionSaveButton.disabled = true
        collectionNameInput.classList.add(
          'swym-wishlist-plus-list-ui-input-has-error',
        )
      } else {
        errorMessage.innerHTML = ''
        errorMessageContainer.style.display = 'none'
        collectionSaveButton.disabled = false
        collectionNameInput.classList.remove(
          'swym-wishlist-plus-list-ui-input-has-error',
        )
      }
    })

    createCollectionForm.addEventListener('submit', async (event) => {
      try {
        event.preventDefault()
        const name = collectionNameInput.value.trim()
        const newList =
          await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.createList(name)
        const { epi, empi, du, iu } = this.wishlistItem
        await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.addProductsToList(
          [{ empi, epi, du }],
          newList.lid,
        )
        this.closeDrawer()
        WishlistPlusListUIExtensions.SwymWishlistPlusListNotification?.setMessage(
          {
            message:
              WishlistPlusListUIContext.SwymWishlistPlusListStrings
                ?.notificationMessageAddedToCollection,
            status:
              WishlistPlusListUIContext
                .SwymWishlistPlusListNotificationStatusTypes?.Toast,
            duration: 5000,
            image: iu,
            action: {
              label:
                WishlistPlusListUIContext.SwymWishlistPlusListStrings
                  ?.notificationActionView,
              onClick: () => {
                window.location.hash =
                  WishlistPlusListUIContext.SwymWishlistPlusListUrls
                    ?.CollectionList + newList.lid
              },
            },
          },
        )
      } catch (error) {
        console.warn('Error on create and add product to list', error)
      }
    })
  }

  async renderEditCollection() {
    if (this.list && this.elements.optionsContainer) {
      this.elements.title.innerHTML = `
        <div class="swym-wishlist-plus-list-ui-action-title">${this.list.lname}</div>
      `
      this.elements.optionsContainer.innerHTML = `
        <div class="swym-wishlist-plus-list-ui-action-btn rename-collection-button">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.renameCollectionCta}</div>
        <div class="swym-wishlist-plus-list-ui-action-btn delete-list-button">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.deleteCollectionCta}</div>
      `
      this.addEditCollectionEventListeners()
    }
  }

  addEditCollectionEventListeners() {
    const renameCollectionBtn = this.querySelector('.rename-collection-button')
    const deleteListBtn = this.querySelector('.delete-list-button')

    renameCollectionBtn?.addEventListener('click', () => {
      this.renderRenameCollection()
    })

    deleteListBtn?.addEventListener('click', async () => {
      try {
        await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.deleteLists([this.list])
        this.closeDrawer()
        window.location.hash =
          WishlistPlusListUIContext.SwymWishlistPlusListUrls?.List
        await WishlistPlusListUIExtensions.SwymWishlistPlusList?.refreshList()
        const collectionListView = document.querySelector(
          'swym-wishlist-plus-list-ui-collection-list',
        )
        collectionListView.closeCollectinListView()

        WishlistPlusListUIExtensions.SwymWishlistPlusListNotification?.setMessage(
          {
            message:
              WishlistPlusListUIContext.SwymWishlistPlusListStrings
                ?.notificationMessageCollectionDeleted,
            status:
              WishlistPlusListUIContext
                .SwymWishlistPlusListNotificationStatusTypes?.Toast,
            iconType:
              WishlistPlusListUIContext.SwymWishlistPlusListNotificationIconType
                ?.SuccessIcon,
          },
        )
      } catch (error) {
        console.warn('Failed to remove product from the list:', error)
      }
    })
  }

  renderRenameCollection() {
    if (this.elements.optionsContainer) {
      this.elements.title.innerHTML = `<div class="swym-wishlist-plus-list-ui-action-title">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.updateCollectionTitle}</div>`
      this.elements.optionsContainer.innerHTML = `
        <form id="swym-wishlist-plus-list-ui-rename-collection-form">
          <div class="swym-wishlist-plus-list-ui-rename-collection-name-form-group">
            <input
              type="text"
              id="swym-wishlist-plus-list-ui-rename-collection-name-input"
              class="swym-wishlist-plus-list-ui-action-input rename-collection-name-input swym-wishlist-plus-list-ui-input-has-value"
              value="${this.list.lname}"
              placeholder=""
            />
            <label for="swym-wishlist-plus-list-ui-rename-collection-name-input" class="swym-wishlist-plus-list-ui-rename-collection-name-label">Name</label>
            <div id="swym-wishlist-plus-list-ui-rename-collection-error-message-container" class="error-message" style="display: none;">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="base-icon error-icon" aria-hidden="true"><path d="M7 .7a6.3 6.3 0 1 0 0 12.6A6.3 6.3 0 0 0 7 .7Zm.7 9.8H6.3V9.1h1.4v1.4Zm0-2.1H6.3V3.5h1.4v4.9Z" fill="currentColor"></path></svg>
              <span id="swym-wishlist-plus-list-ui-rename-collection-error-message">Name is required</span>
            </div>
          </div>
          <button type="submit" id="swym-wishlist-plus-list-ui-rename-collection-list-button" class="swym-wishlist-plus-list-ui-save-collection-list-button" disabled>${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.saveNewCollectionCta}</button>
        </form>
      `
      this.addRenameCollectionEventListeners()
    }
  }

  addRenameCollectionEventListeners() {
    const renameCollectionForm = this.querySelector(
      '#swym-wishlist-plus-list-ui-rename-collection-form',
    )
    const collectionNameInput = this.querySelector(
      '#swym-wishlist-plus-list-ui-rename-collection-name-input',
    )
    const collectionRenameButton = this.querySelector(
      '#swym-wishlist-plus-list-ui-rename-collection-list-button',
    )
    const errorMessageContainer = this.querySelector(
      '#swym-wishlist-plus-list-ui-rename-collection-error-message-container',
    )
    const errorMessage = this.querySelector(
      '#swym-wishlist-plus-list-ui-rename-collection-error-message',
    )

    collectionNameInput.addEventListener('input', () => {
      const name = collectionNameInput.value.trim()
      let error

      const isNameExist = this.collections.some(
        (collection) => collection.lname === name,
      )

      if (name === '') {
        error =
          WishlistPlusListUIContext.SwymWishlistPlusListStrings
            ?.errorMessageListNameRequired
      } else if (name.length < 3) {
        error =
          WishlistPlusListUIContext.SwymWishlistPlusListStrings
            ?.errorMessageListNameRequire3Char
      } else if (isNameExist) {
        error =
          WishlistPlusListUIContext.SwymWishlistPlusListStrings
            ?.errorMessageListNameAlreadyExist
      }

      if (name.length > 0) {
        collectionNameInput.classList.add(
          'swym-wishlist-plus-list-ui-input-has-value',
        )
      } else {
        collectionNameInput.classList.remove(
          'swym-wishlist-plus-list-ui-input-has-value',
        )
      }

      if (error) {
        errorMessage.innerHTML = error
        errorMessageContainer.style.display = 'flex'
        collectionRenameButton.disabled = true
        collectionNameInput.classList.add(
          'swym-wishlist-plus-list-ui-input-has-error',
        )
      } else {
        errorMessage.innerHTML = ''
        errorMessageContainer.style.display = 'none'
        collectionRenameButton.disabled = false
        collectionNameInput.classList.remove(
          'swym-wishlist-plus-list-ui-input-has-error',
        )
      }
    })

    renameCollectionForm.addEventListener('submit', async (event) => {
      try {
        event.preventDefault()
        const name = collectionNameInput.value.trim()
        await WishlistPlusUILayoutAPI?.SwymWLAsyncApis?.renameList(
          this.listId,
          name,
        )
        this.closeDrawer()
        await WishlistPlusListUIExtensions.SwymWishlistPlusList?.refreshList()
        const collectionListView = document.querySelector(
          'swym-wishlist-plus-list-ui-collection-list',
        )
        collectionListView.closeCollectinListView()

        WishlistPlusListUIExtensions.SwymWishlistPlusListNotification?.setMessage(
          {
            message:
              WishlistPlusListUIContext.SwymWishlistPlusListStrings
                ?.notificationMessageCollectionUpdated,
            status:
              WishlistPlusListUIContext
                .SwymWishlistPlusListNotificationStatusTypes?.Toast,
            duration: 5000,
            iconType:
              WishlistPlusListUIContext.SwymWishlistPlusListNotificationIconType
                ?.SuccessIcon,
            action: {
              label:
                WishlistPlusListUIContext.SwymWishlistPlusListStrings
                  ?.notificationActionView,
              onClick: () => {
                window.location.hash =
                  WishlistPlusListUIContext.SwymWishlistPlusListUrls
                    ?.CollectionList + this.listId
              },
            },
          },
        )
      } catch (error) {
        console.warn('Error on rename and add product to list', error)
      }
    })
  }

  addEventListenerToView() {
    this.elements.backdrop.addEventListener('click', () => this.closeDrawer())
    this.elements.closeButton.addEventListener('click', () =>
      this.closeDrawer(),
    )
  }

  openDrawer() {
    this.elements.container.classList.add(
      'swym-wishlist-plus-list-ui-show-action-view',
    )
  }

  closeDrawer() {
    this.elements.container.classList.remove(
      'swym-wishlist-plus-list-ui-show-action-view',
    )
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-actions',
  SwymWishlistPlusListActions,
)

class SwymWishlistPlusListNotification extends HTMLElement {
  constructor() {
    super()
    this.timeout = null
  }

  connectedCallback() {
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'alert')
    }
    if (!this.hasAttribute('status')) {
      this.setAttribute('status', 'info')
    }
  }

  setMessage({
    message,
    status = 'success',
    duration = 3000,
    action = null,
    image = null,
    icon = null,
    iconType = null,
  }) {
    const isNotificationEnabled =
      WishlistPlusListUIContext.WishlistPlusListConfig
        ?.EnableWishlistPlusListNotification ?? false
    if (isNotificationEnabled) {
      this.setAttribute('status', status)
      this.render({ message, action, image, icon, iconType })
      this.autoHide(duration)
    }
  }

  render({
    message = this.textContent,
    action = null,
    image = null,
    icon = null,
    iconType = null,
  } = {}) {
    if (!message) return

    const imageHtml = image
      ? `<div class="swym-wishlist-plus-list-ui-notification-image-container">
           <img class="swym-wishlist-plus-list-ui-notification-image" src="${image}" alt="Notification Icon">
         </div>`
      : ''

    const iconHtml = iconType
      ? `<div class="swym-wishlist-plus-list-ui-notification-icon-container">
          ${iconType === WishlistPlusListUIContext.SwymWishlistPlusListNotificationIconType?.SuccessIcon && `<svg data-v-bfb713a3="" data-v-650c2660="" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="base-icon toast-icon" aria-hidden="true"><path d="m12.053 5.429-.62.645-3.782 3.98-1.131-1.04-.654-.602-1.152 1.384.661.603 1.745 1.606.613.566 5.553-5.844-1.233-1.298Z" fill="currentColor"></path></svg>`}
         </div>`
      : ''

    const actionHtml = action
      ? `<div class="swym-wishlist-plus-list-ui-notification-action">${action.label}</div>`
      : ''

    this.innerHTML = `
      ${imageHtml ? imageHtml : iconHtml}
      <div class="swym-wishlist-plus-list-ui-notification-content">${message}</div>
      ${actionHtml}
    `

    if (action) {
      const originalOnClick = action.onClick

      const notificationAction = this.querySelector(
        '.swym-wishlist-plus-list-ui-notification-action',
      )

      action.onClick = (event) => {
        this.hideNotification()
        originalOnClick(event)
      }

      notificationAction?.removeEventListener('click', originalOnClick)
      notificationAction?.addEventListener('click', action.onClick)
    }
  }

  autoHide(duration = 3000) {
    clearTimeout(this.timeout)
    this.showNotification()

    this.timeout = setTimeout(() => {
      this.hideNotification()
    }, duration)
  }

  showNotification() {
    this.classList.add('visible')
  }

  hideNotification() {
    this.classList.remove('visible')
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-notification',
  SwymWishlistPlusListNotification,
)

class SwymWishlistPlusListLoader extends HTMLElement {
  constructor() {
    super()

    this.renderLoader()
  }

  renderLoader() {
    this.innerHTML = `
    <div class="swym-wishlist-plus-list-ui-loader-container">
      <svg version="1.1" id="L9" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
        viewBox="0 0 100 100" enable-background="new 0 0 0 0" xml:space="preserve">
          <path fill="currentColor" d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
            <animateTransform 
              attributeName="transform" 
              attributeType="XML" 
              type="rotate"
              dur="1s" 
              from="0 50 50"
              to="360 50 50" 
              repeatCount="indefinite" />
        </path>
      </svg>
    </div>
    `
  }

  static get observedAttributes() {
    return ['loading', 'width', 'height']
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'loading') {
      if (newValue === 'true') {
        this.style.display = 'flex'
      } else {
        this.style.display = 'none'
      }
    } else if (name === 'width') {
      this.style.width = newValue
    } else if (name === 'height') {
      this.style.height = newValue
    }
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-loader',
  SwymWishlistPlusListLoader,
)

class SwymWishlistPlusListLoggedUser extends HTMLElement {
  constructor() {
    super()
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = []
    }
    window.SwymCallbacks.push(this.swymCallbackFn.bind(this))
  }

  swymCallbackFn(swat) {
    const showUser = this.getAttribute('showuser') === 'true'
    const showLogin = this.getAttribute('showLogin') === 'true'
    const isUserLogged = !!window.swymCustomerId

    if (!isUserLogged && showLogin) {
      this.renderLoginContainer()
    } else if (isUserLogged && showUser) {
      this.renderUserDetails()
    }
  }

  renderUserDetails() {
    const customer = WishlistPlusListUIContext.SwymWlCustomerData
    this.innerHTML = `
      <div class="swym-wishlist-plus-list-ui-logged-user-container">
        <div class="swym-wishlist-plus-list-ui-logged-user-welcome-message">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.loggedUserWelcomeMessage} <span class="swym-wishlist-plus-list-ui-logged-user-name">${customer?.name ? customer?.name : customer?.email}</span></div>
      </div>
    `
  }

  renderLoginContainer() {
    this.innerHTML = `
      <div class="swym-wishlist-plus-list-ui-login-user-container">
        <div class="swym-wishlist-plus-list-ui-login-description">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.loginText}</div>
        <a href="${window.location.origin}/account" class="swym-wishlist-plus-list-ui-login-button">${WishlistPlusListUIContext.SwymWishlistPlusListStrings?.loginButtonText}</a>
      </div>
    `
  }
}

customElements.define(
  'swym-wishlist-plus-list-ui-login-user',
  SwymWishlistPlusListLoggedUser,
)
